export type Employee = {
  id: number
  firstName: string
  lastName: string
  firstNameKana: string
  lastNameKana: string
  code: string
  languageCode: string
  idCardCode: string
  gender: string
  companyJoinDate?: Date
  dob?: Date
  position: string
  department: string
  contractHours: number
  workingForm: number
  tagCode: string
  shiftType: string
}

export type DetergentHistory = {
  id: number
  quantity: number
  price: number
  detergentId: number
  createdAt: string
  updatedAt: string
}

export type Detergent = {
  id: number
  price: number
  totalQuantity: number
  remainingQuantity: number
  replacementDate: string
  isWarning: boolean
  name: string
  netWeightG: number
  netWeightMl: number
  specificGravity: number
  costPerMl: number
  thresholdQuantity: number
  usedQuantity: number
  isWater: boolean
  isSendMail: boolean
  tanks: Tank[]
  createdAt: string
  updatedAt: string
}

export type WashingMethodDetergent = {
  id: number
  name: string
  quantity: number
  washingMethodId?: number
  detergentId?: number
  detergent: Detergent
  createdAt: Date
  updatedAt: Date
}

export type WashingMethodCustomer = {
  id: number
  customerId: number
  washingMethodId: number
  customer: Customer[]
  createdAt: Date
  updatedAt: Date
}

export type WashingMethod = {
  id: number
  name: string
  time: number
  code: number
  volume: number
  cost: number
  waterQuantity: number
  washingMethodCustomers: WashingMethodCustomer[]
  washingMethodDetergents: WashingMethodDetergent[]
  createdAt: Date
  updatedAt: Date
}

const CustomerType = {
  CUSTOMER: 0,
  OPERATOR: 1,
} as const

type CustomerType = typeof CustomerType[keyof typeof CustomerType]

export type Customer = {
  id: number
  code?: string
  shortName: string
  name: string
  type: CustomerType
  washingMethods: WashingMethod[]
  createdAt: Date
  updatedAt: Date
  deletedAt?: Date
}

export type IdCard = {
  id: number
  code: string
}

export type Terminal = {
  id: number
  name: string
  description: string
}

export type LogType = 'checkIn' | 'checkOut' | 'takeOut'

export type AttendanceLog = {
  id: number
  createdAt: Date
  returnedAt: Date
  uniformCode: string
  employeeId: number
  firstName: string
  firstNameKana: string
  lastName: string
  lastNameKana: string
  terminal: Terminal
  uniformId: number
  type: LogType
  productCode: Uniform
  productName: Uniform
  size: Uniform
  employeeCode: string
  barcode: Uniform
}

export type AttendanceLogExport = {
  id: number
  createdAt: Date
  size: number
  fileName: string
  url: string
}

export type Incident = {
  id: number
  message: string
  path: string
}

export type ClientDevice = {
  id: number
  macAddress: string
  name: string
  lastPingAt: string
  isSendMail: boolean
  order: number | null
  isOffline: boolean
}

export type Uniform = {
  id: number
  code: string
  attendanceLogs?: AttendanceLog[]
  productName: string
  productCode: string
  barcode: string
  size: string
}

export type AnnouncementSpecialType = 'happyBirthday' | 'joiningCompany'

export type AnnouncementFilter = {
  genders: Employee['gender'][]
  languages: Language['code'][]
}

export type Announcement = {
  id: number
  text: string
  startAt: Date
  endAt: Date
  isPublic: boolean
  isPublished: boolean
  isQuestion: boolean
  specialType?: AnnouncementSpecialType
  employees: Employee[]
  languageFilter?: string[]
  genderFilter?: string[]
}

export type Language = {
  id: number
  code: string
  name: string
  isDefault: boolean
}

export type Translation = {
  id: number
  text: string
  language: Language
}

export type Login = {
  userId: string
  password: string
}

export type Admin = {
  id: number
  firstName: string
  lastName: string
  firstNameKana: string
  lastNameKana: string
  userId: string
  password: string
}

const DateType = {
  NO_SELECT: 0,
  BY_DAY: 1,
  RANGE_DAY: 2,
  TOTAL_RANGE_DAY: 3,
} as const

type DateType = typeof DateType[keyof typeof DateType]

export { DateType }

export type MachinePartHistory = {
  id: number
  washingPartId: number
  totalTime: number
  washingPart?: MachinePart
  replacementDate: string
  createdAt: string
  updatedAt: string
}

export type MachinePart = {
  id: number
  washingMachineId: number
  washingMachine: string
  washingHistories: MachinePartHistory[]
  washingMachinePartId: number
  washingMachinePart: WashingMachinePart
  name: string
  totalTime: number
  totalLife: number
  isWarning: boolean
  isSendMail: boolean
  replacementDate: string
  createdAt: string
  updatedAt: string
  deletedAt: string
}

export type Machine = {
  id: number
  name: string
  type: number
  typeName: string
  amount: number
  totalTime: number
  isWorking: boolean
  isWarning: boolean
  startedAt: string
  laundryTime: number
  machineParts: MachinePart[]
  tanks: Tank[]
  createdAt: string
  updatedAt: string
  deletedAt: string
}

export type Notification = {
  id: number
  message: string
  path: string
  isRead: boolean
  createdAt: string
  updatedAt: string
}

export type WashingMachinePart = {
  id: number
  name: string
  manufacturer: string
  supplier: string
  price: number
  inventory: number
  createdAt: string
  updatedAt: string
}

const TankMethodType = {
  MANUAL: 0,
  AUTOMATIC: 1,
} as const

type TankMethodType = typeof TankMethodType[keyof typeof TankMethodType]

export { TankMethodType }

export type Tank = {
  id: number
  isWarning: boolean
  tankNo: number
  method: TankMethodType
  thresholdQuantity: number
  usedQuantity: number
  remainingQuantity: number
  detergents: Detergent[]
  isSendMail: false
  createdAt: string
  updatedAt: string
}

export type TankHistory = {
  id: number
  quantity: number
  caseMl: number
  tankId: number
  createdAt: string
  updatedAt: string
}

export type SuccessResponse = {
  statusCode: number
  message: string
}

export type ImportCsvRequest = FormData

type Error = {
  [key: string]: string
}

export type ImportCsvResponse = {
  failedToImportRecords: Error[]
}
